import React, { useEffect, useMemo, useState, useRef } from "react";
import { Modal, Form, Input, Select, Row, Col, Button, Alert } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { formatLabel, textFieldRules, selectFieldRules } from "../../utils";
import {
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useGetServicesByPoIdQuery,
} from "../../store/api/serviceApi";
import { useGetUsersQuery } from "../../store/api/userApi";
import { useGetEntityFilesQuery } from "../../store/api/fileApi";
import { useDebounce } from "../../hooks";
import { useToast } from "../../hooks/useToast";
import S3FileUpload, { S3FileUploadRef } from "./S3FileUpload";
import UploadedFilesList from "./UploadedFilesList";
import type {
  ServiceResponse,
  DispatchResponse,
} from "@OrianaTypes";

const PRECOMMISSIONING_ENTITY_TYPE = "precommissioning";

interface ServiceFormModalProps {
  visible: boolean;
  onClose: () => void;
  poId: string;
  dispatches: DispatchResponse[];
  editData?: ServiceResponse | null;
}

const ServiceFormModal: React.FC<ServiceFormModalProps> = ({
  visible,
  onClose,
  poId,
  dispatches,
  editData = null,
}) => {
  const [form] = Form.useForm();
  const toast = useToast();

  const isEditMode = !!editData;

  // Single file upload for Pre-Commissioning (multiple files, same as delivery confirmation)
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const fileUploadRef = useRef<S3FileUploadRef>(null);

  const serviceIdForFiles = editData?.serviceId != null ? String(editData.serviceId) : "";
  const { data: precommissioningFilesRes } = useGetEntityFilesQuery(
    { entityType: PRECOMMISSIONING_ENTITY_TYPE, entityId: serviceIdForFiles },
    { skip: !serviceIdForFiles || !visible }
  );
  const precommissioningFiles = precommissioningFilesRes?.data ?? [];

  // Get existing services to filter out already used serial numbers
  const { data: existingServices = [] } = useGetServicesByPoIdQuery(poId, {
    skip: !visible || !poId,
  });

  const [userSearchTerm, setUserSearchTerm] = useState<string>("");
  const debouncedUserSearchTerm = useDebounce(userSearchTerm, 300);

  const { data: usersResponse } = useGetUsersQuery({
    page: 1,
    limit: 10,
    searchKey: debouncedUserSearchTerm ? "username" : undefined,
    searchTerm: debouncedUserSearchTerm || undefined,
    sortBy: "username",
    sortOrder: "ASC",
  });
  const userOptions = useMemo(() => {
    const usersData = usersResponse?.data ?? [];
    return usersData
      .filter((u: { isActive?: boolean }) => u.isActive !== false)
      .map((u: { userId: number; username: string }) => ({
        value: u.userId,
        label: u.username,
      }));
  }, [usersResponse?.data]);

  const [createService, { isLoading: isCreating }] = useCreateServiceMutation();
  const [updateService, { isLoading: isUpdating }] = useUpdateServiceMutation();

  // Status options - using lowercase to match backend
  const statusOptions = [
    { value: "done", label: "Done" },
    { value: "pending", label: "Pending" },
    { value: "hold", label: "Hold" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const ppmConfirmationStatusOptions = [
    { value: "done", label: "Done" },
    { value: "pending", label: "Pending" },
    { value: "hold", label: "Hold" },
    { value: "cancelled", label: "Cancelled" },
  ];

  // Derive eligible serial numbers from dispatches with deliveryStatus === 'done'
  const eligibleSerials = useMemo(() => {
    const eligible: Array<{
      dispatchId: number;
      productId: number;
      productName: string;
      serialNumber: string;
      warranty?: string;
    }> = [];

    // Get dispatches with delivery done
    const dispatchesWithDelivery = dispatches.filter(
      (d) => d.deliveryStatus === "done"
    );

    // Get existing serial numbers that already have services
    const existingSerialKeys = new Set(
      existingServices.map((s) => `${s.dispatchId}-${s.serialNumber}`)
    );

    // Extract serial numbers from dispatched items
    dispatchesWithDelivery.forEach((dispatch) => {
      dispatch.dispatchedItems?.forEach((item) => {
        if (item.serialNumbers) {
          // Serial numbers are comma-separated
          const serials = item.serialNumbers
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s !== "");

          serials.forEach((serialNumber) => {
            const key = `${dispatch.dispatchId}-${serialNumber}`;
            // Only include if not already used
            if (!existingSerialKeys.has(key)) {
              eligible.push({
                dispatchId: dispatch.dispatchId,
                productId: item.productId,
                productName: item.productName || "",
                warranty: item.warranty ?? "",
                serialNumber,
              });
            }
          });
        }
      });
    });

    return eligible;
  }, [dispatches, existingServices]);

  const serialOptions = useMemo(() => {
    if (isEditMode && editData) {
      return [
        {
          value: `${editData.dispatchId}__${editData.productName}__${editData.serialNumber}__${editData.warranty ?? ""}`,
          label: `${editData.serialNumber} (${formatLabel(editData.productName)} - #${editData.dispatchId})`,
        },
      ];
    }
    return eligibleSerials.map((s) => ({
      value: `${s.dispatchId}__${s.productName}__${s.serialNumber}__${s.warranty}`,
      label: `${s.serialNumber} (${formatLabel(s.productName)} - #${s.dispatchId})`,
    }));
  }, [eligibleSerials, isEditMode, editData]);

  useEffect(() => {
    if (visible && editData) {
      form.setFieldsValue({
        serialNumber: `${editData.dispatchId}__${editData.productName}__${editData.serialNumber}__${editData.warranty}`,
        pcContact: editData.pcContact,
        assignedServiceEngineerId: editData.assignedServiceEngineerId,
        ppmChecklist: editData.ppmChecklist,
        ppmSheetReceivedFromClient: editData.ppmSheetReceivedFromClient,
        ppmChecklistSharedWithOem: editData.ppmChecklistSharedWithOem,
        ppmTickedNoFromOem: editData.ppmTickedNoFromOem,
        ppmConfirmationStatus: editData.ppmConfirmationStatus,
        oemComments: editData.oemComments,
        preCommissioningStatus: editData.preCommissioningStatus,
        remarks: editData.remarks,
      });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, editData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let serviceId: number;

      if (isEditMode && editData) {
        await updateService({
          id: editData.serviceId,
          data: {
            serialNumber: editData.serialNumber,
            pcContact: values.pcContact,
            assignedServiceEngineerId:
              values.assignedServiceEngineerId != null && values.assignedServiceEngineerId !== ""
                ? Number(values.assignedServiceEngineerId)
                : undefined,
            ppmChecklist: values.ppmChecklist,
            ppmSheetReceivedFromClient: values.ppmSheetReceivedFromClient,
            ppmChecklistSharedWithOem: values.ppmChecklistSharedWithOem,
            ppmTickedNoFromOem: values.ppmTickedNoFromOem,
            ppmConfirmationStatus: values.ppmConfirmationStatus,
            oemComments: values.oemComments || undefined,
            preCommissioningStatus: values.preCommissioningStatus,
            remarks: values.remarks || undefined,
          },
          poId,
        }).unwrap();
        serviceId = editData.serviceId;
        toast.success("Service updated successfully");
      } else {
        if (!values.serialNumber) {
          toast.error("Please select a serial number");
          return;
        }
        const [dispatchId, productName, serialNumber, warranty] = values.serialNumber.split("__");
        const result = await createService({
          dispatchId: parseInt(dispatchId, 10),
          serialNumber,
          productName,
          warranty,
          pcContact: values.pcContact,
          assignedServiceEngineerId:
            values.assignedServiceEngineerId != null && values.assignedServiceEngineerId !== ""
              ? Number(values.assignedServiceEngineerId)
              : undefined,
          ppmChecklist: values.ppmChecklist,
          ppmSheetReceivedFromClient: values.ppmSheetReceivedFromClient,
          ppmChecklistSharedWithOem: values.ppmChecklistSharedWithOem,
          ppmTickedNoFromOem: values.ppmTickedNoFromOem,
          ppmConfirmationStatus: values.ppmConfirmationStatus,
          oemComments: values.oemComments || undefined,
          preCommissioningStatus: values.preCommissioningStatus,
          remarks: values.remarks || undefined,
          poId,
        }).unwrap();
        serviceId = result?.serviceId;
        toast.success("Service created successfully");
      }

      if (
        serviceId != null &&
        fileList.some((f) => f.originFileObj) &&
        fileUploadRef.current
      ) {
        try {
          await fileUploadRef.current.uploadAndConfirm({
            poId,
            entityType: PRECOMMISSIONING_ENTITY_TYPE,
            entityId: String(serviceId),
          });
        } catch (e) {
          console.warn("Pre-Commissioning file upload failed:", e);
          toast.warning("Service saved but file upload failed.");
        }
      }
      handleCancel();
    } catch (error) {
      console.error("Submission failed:", error);
      toast.error(
        isEditMode
          ? "Failed to update service"
          : "Failed to create service"
      );
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <Modal
      title={isEditMode ? "Edit Service (Pre-Commissioning)" : "Add Service (Pre-Commissioning)"}
      open={visible}
      onCancel={handleCancel}
      width={900}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          style={{ backgroundColor: "#4b6cb7" }}
        >
          {isEditMode ? "Update" : "Submit"}
        </Button>,
      ]}
    >
      {!isEditMode && serialOptions.length === 0 ? (
        <Alert
          type="info"
          message="No Eligible Serial Numbers"
          description="Serial numbers become eligible when dispatch delivery status is 'done' and they haven't been used for service creation yet."
          showIcon
        />
      ) : (
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          style={{ marginTop: "1rem" }}
        >
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                name="serialNumber"
                label="Serial Number"
                rules={[{ required: !isEditMode, message: "Select a serial number" }]}
              >
                <Select
                  placeholder="Select serial number"
                  options={serialOptions}
                  disabled={isEditMode}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="pcContact"
                label="PC Contact"
                rules={textFieldRules}
              >
                <Input placeholder="Client side co-ordinator" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="assignedServiceEngineerId"
                label="Service Engineer"
                rules={selectFieldRules}
              >
                <Select
                  placeholder="Select service engineer"
                  options={userOptions}
                  allowClear
                  showSearch
                  filterOption={false}
                  onSearch={(value) => setUserSearchTerm(value)}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="ppmChecklist"
                label="PPM/Checklist Shared with Customer"
                rules={textFieldRules}
              >
                <Input placeholder="PPM/Checklist details" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="ppmSheetReceivedFromClient"
                label="PPM Sheet Received"
                rules={textFieldRules}
              >
                <Input placeholder="PPM sheet received details" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="ppmChecklistSharedWithOem"
                label="PPM Shared with OEM"
                rules={textFieldRules}
              >
                <Input placeholder="Details" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="ppmTickedNoFromOem"
                label="PPM Ticket No from OEM"
                rules={textFieldRules}
              >
                <Input placeholder="PPM ticket no" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="ppmConfirmationStatus"
                label="PPM Confirmation Status"
                rules={selectFieldRules}
              >
                <Select
                  placeholder="Select status"
                  options={ppmConfirmationStatusOptions}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="preCommissioningStatus"
                label="Pre-commissioning Status"
                rules={selectFieldRules}
              >
                <Select placeholder="Select status" options={statusOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item name="oemComments" label="Comment from OEM">
                <Input.TextArea placeholder="Optional" rows={2} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item name="remarks" label="Remarks">
                <Input.TextArea placeholder="Enter remarks" rows={2} />
              </Form.Item>
            </Col>
          </Row>

          {/* Upload Pre-Commissioning documents (multiple files, same as delivery confirmation) */}
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label="Upload Document (Pre-Commissioning)"
                tooltip="Upload documents e.g. installation images, Pre-Post Commissioning, SLD, PPM sheet. Supported formats: Images, PDF, Word, Excel"
              >
                <S3FileUpload
                  ref={fileUploadRef}
                  fileList={fileList}
                  onChange={setFileList}
                  minFiles={0}
                  maxFiles={10}
                  maxSizeMB={10}
                  label=""
                  buttonLabel="Click to Upload"
                  helperText="Supported: Images (JPG, PNG, GIF, SVG), PDF, Word, Excel."
                  poId={poId}
                  entityType={PRECOMMISSIONING_ENTITY_TYPE}
                  entityId={serviceIdForFiles || undefined}
                />
                <UploadedFilesList
                  files={precommissioningFiles}
                  title="Pre-Commissioning Documents"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default ServiceFormModal;

